import { constants } from '../../constants/Constants';

export const formatValueWithMiddlewares = (value, middlewares) => {
  const applyMiddleware = (index, value) => {
    if (index >= middlewares.length) {
      return value;
    }
    return middlewares[index](value, (nextValue) =>
      applyMiddleware(index + 1, nextValue)
    );
  };
  return applyMiddleware(0, value);
};

export const checkForNA = (value, next) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return 'NA';
  }
  return next ? next(value) : value;
};

export const removeTrailingZero = (value, next) => {
  const numericValue = Number(value);
  if (next) {
    let result;
    if (numericValue % 1 === 0) {
      result = numericValue.toFixed(0);
    } else {
      result = numericValue.toString();
    }
    return next(result);
  } else {
    return value;
  }
};

export const replaceNegativeSign = (value, next) => {
  if (value === null || value === undefined) {
    return 'NA';
  }
  const stringValue = value.toString();
  const formattedValue = stringValue.replace('-', '');
  return next ? next(formattedValue) : formattedValue;
};

export const applySuffix = (value, next) => {
  const numericValue = Number(value);
  const isNegative = numericValue < 0;
  const absValue = Math.abs(numericValue);
  let formattedValue;

  if (absValue >= 1e12) {
    formattedValue = { value: (absValue / 1e12).toFixed(1), suffix: 'T' };
  } else if (absValue >= 1e9) {
    formattedValue = { value: (absValue / 1e9).toFixed(1), suffix: 'B' };
  } else if (absValue >= 1e6) {
    formattedValue = { value: (absValue / 1e6).toFixed(1), suffix: 'M' };
  } else if (absValue >= 1e3) {
    formattedValue = { value: (absValue / 1e3).toFixed(1), suffix: 'K' };
  } else {
    formattedValue = { value: absValue, suffix: '' };
  }

  // Convert to integer if the decimal part is zero.
  if (formattedValue.value % 1 === 0) {
    formattedValue.value = parseInt(formattedValue.value);
  }

  // Reapply the negative sign if necessary.
  if (isNegative) {
    formattedValue.value = '-' + formattedValue.value;
  }

  return next ? next(`${formattedValue.value}${formattedValue.suffix}`) : value;
};

export const appendPercentage = (value, next) => {
  const numericValue = Number(value);
  return next ? next(`${numericValue}%`) : `${numericValue}%`;
};

export const appendHours = (value, next) => {
  const formattedValue = `${value} h`;
  return next ? next(formattedValue) : formattedValue;
};

export const appendKgPerHour = (value, next) => {
  const formattedValue = `${value} kg/h`;
  return next ? next(formattedValue) : formattedValue;
};

export const convertBytesToMB = (value, next) => {
  const numericValue = Number(value);
  const mbValue = numericValue / (1024 * 1024);
  return next ? next(mbValue) : mbValue;
};

export const appendMB = (value, next) => {
  const formattedValue = `${value} MB`;
  return next ? next(formattedValue) : formattedValue;
};

export const applyDollarSign = (value, next) => {
  const formattedValue = `$${value}`;
  return next ? next(formattedValue) : formattedValue;
};

export const roundToTwoDecimalPlaces = (value, next) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return 'NA';
  }

  const numericValue = Number(value);
  const roundedValue = numericValue.toFixed(2);

  return next ? next(roundedValue) : roundedValue;
};

export const createKpiTileItem = (
  title,
  value,
  tag,
  subTitle = null,
  secondTitle = null,
  showViewDetails = false
) => {
  let tags = [];

  if (Array.isArray(tag)) {
    // Filter out tags with text 'NA'
    tags = tag.filter((t) => t.text !== 'NA');
  } else if (tag && tag.text !== 'NA') {
    tags = [tag];
  }

  return {
    title,
    secondTitle,
    value,
    tag: tags.length > 0 ? tags : null,
    subTitle,
    showViewDetails,
  };
};

const period = {
  last24hours: 1,
  daily: 7,
  weekly: 28,
  monthly: 182,
  yearly: 365,
};

export const parseDateTimeBasedOnPeriodType = (value, p) => {
  const date = new Date(value);
  const monthNumber = date.getUTCMonth() + 1;
  const dayValue = date.getUTCDate();
  const yearValue = date.getUTCFullYear();

  switch (p) {
    case period?.last24hours:
      return utcTimeToHourAndMinute(value);
    case period?.daily:
      return getDateAndMonth(value);
    case period?.weekly:
      return `Wk ${monthNumber}/${dayValue}`;
    case period?.monthly:
      return getMonthShortFromDateString(value);
    case period?.yearly:
      return String(yearValue);
    default:
      return '';
  }
};

export const toPascalCase = (value, next) => {
  if (typeof value !== 'string') {
    // Be explicit about the check for next to improve test coverage
    if (next && typeof next === 'function') {
      return next(value);
    }
    return value;
  }

  // Convert to lowercase and handle the case where toLowerCase might return null
  const lowerCaseValue = String(value).toLowerCase();

  // Simplify to avoid complex branching
  const pascalCaseValue = lowerCaseValue.replace(/\b\w/g, (char) =>
    char.toUpperCase()
  );

  // Be explicit about the check for next to improve test coverage
  if (next && typeof next === 'function') {
    return next(pascalCaseValue);
  }
  return pascalCaseValue;
};

export const toOneDecimalPlace = (value, next) => {
  // Convert the input to a Number
  const numericValue = Number(value);

  // Round the value to one decimal place.
  // Multiplying by 10 shifts the decimal, rounding,
  // then dividing by 10 shifts the decimal back.
  const oneDecimalValue = Math.round(numericValue * 10) / 10;

  // If a next middleware function is provided, pass the one decimal value to it;
  // otherwise, simply return the one decimal value.
  return next ? next(oneDecimalValue) : oneDecimalValue;
};

//===================================
// Non  Exported Functions
//===================================

function getMonthShortFromDateString(dateTime) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  return formatter.format(new Date(dateTime));
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

function getDateAndMonth(dateTime) {
  return dateFormatter.format(new Date(dateTime));
}

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});

function utcTimeToHourAndMinute(date) {
  return timeFormatter.format(new Date(date));
}

export const formatDollarValue = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    removeTrailingZero,
    applySuffix,
    applyDollarSign,
  ]);

export const formatPercentageValue = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    removeTrailingZero,
    appendPercentage,
  ]);

export const formatPecentageValueWithOneDecimal = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    toOneDecimalPlace,
    appendPercentage,
  ]);

export const formatNormalNumber = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    removeTrailingZero,
    applySuffix,
  ]);

export const formatHoursValue = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    toOneDecimalPlace,
    appendHours,
  ]);

export const formatKgPerHourValue = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    toOneDecimalPlace,
    appendKgPerHour,
  ]);

export const formatBytesToMB = (value) =>
  formatValueWithMiddlewares(value, [
    checkForNA,
    convertBytesToMB,
    roundToTwoDecimalPlaces,
    appendMB,
  ]);

export const getKeyByValue = (obj, value) => {
  return Object.keys(obj).find((key) => obj[key] === value);
};

function isInvalidValue(value) {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    value === 'NA' ||
    value === 'null' ||
    value === 'undefined'
  );
}

export const formatTimeWithSeconds = (value) => {
  if (isInvalidValue(value)) {
    return 'NA';
  }

  // Process value based on size
  if (value < 60) {
    value = parseFloat(value);
  } else {
    value = Math.round(value);
  }

  // Define time unit labels once
  const timeLabels = {
    hour: 'h',
    minute: 'm',
    second: 's',
  };

  if (value < 60) {
    // Less than 60 seconds
    return `${value} ${timeLabels.second}`;
  } else if (value < 3600) {
    // Between 60 seconds and 3600 seconds
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;

    return seconds === 0
      ? `${minutes} ${timeLabels.minute}`
      : `${minutes} ${timeLabels.minute} ${seconds} ${timeLabels.second}`;
  } else if (value >= 3601 && value <= 3659) {
    // Special case for 3601 to 3659 seconds
    const hours = 1;
    const seconds = value - 3600;

    return `${hours} ${timeLabels.hour} ${seconds} ${timeLabels.second}`;
  } else {
    // More than 3600 seconds
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);

    return minutes === 0
      ? `${hours} ${timeLabels.hour}`
      : `${hours} ${timeLabels.hour} ${minutes} ${timeLabels.minute}`;
  }
};

export const formatTimeWithoutSeconds = (value) => {
  if (isInvalidValue(value)) {
    return 'NA';
  }

  if (value < 60) {
    // Less than 60 seconds - show as 0 m
    return '0 m';
  } else if (value < 3600) {
    // Between 60 seconds and 3600 seconds
    const minutes = Math.floor(value / 60);
    return `${minutes} m`;
  } else {
    // 3600 seconds or more
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    if (minutes === 0) {
      return `${hours} h`;
    }
    return `${hours} h ${minutes} m`;
  }
};

export const getIndicatorColors = () => {
  return {
    red: '#D40020',
    yellow: '#CC6E16',
    green: '#008553',
  };
};

/**
 * Creates a KPI tile with standardized formatting
 * @param {Object} data - The API response data
 * @param {boolean} ytdCheckbox - Whether YTD comparison is selected
 * @param {Object} options - Configuration options
 * @param {string} options.metricName - The metric name to find in the API response
 * @param {string} options.displayName - The display name for the KPI tile
 * @param {Function} options.formatValue - Function to format the value (defaults to no formatting)
 * @param {Function} options.formatComparisonValue - Function to format comparison values (defaults to percentage)
 * @param {boolean} options.useTagsArray - Whether to return tags as an array (defaults to false)
 * @returns {Object} The formatted KPI tile item
 */
export const createStandardKpiTile = (data, ytdCheckbox, options) => {
  const {
    metricName,
    displayName,
    formatValue = (val) => val,
    formatComparisonValue = formatPercentageValue,
    useTagsArray = false,
  } = options;

  // Find the metric by name in the response data
  const metric = data?.getSteerCoReportingSummaryCard?.data?.find(
    (item) => item?.metricName.trim().toLowerCase() === metricName.toLowerCase()
  );
  if (!metric) {
    return createKpiTileItem(displayName, 'NA', null);
  }

  // Get actual value
  const actualValue = metric.metricValues.find(
    (val) => val.displayName === 'ACTUALS'
  )?.value;
  const actualValueColor = metric.metricValues.find(
    (val) => val.displayName === 'ACTUALS'
  )?.valueDisplayColour;

  // Get period comparison and CAR values
  const periodComparison = metric.metricValues.find(
    (val) => val.displayName === 'PeriodComparison'
  );

  const vsCAR = metric.metricValues.find((val) => val.displayName === 'CAR');

  // Format the value
  const value = formatValue(actualValue);

  // Get color from API data
  let colorToUse = null;
  if (actualValueColor) {
    const colors = getIndicatorColors();
    const colorLower = actualValueColor.toLowerCase();

    // Simplify color detection to avoid hard-to-test branches
    if (colorLower.includes('green')) {
      colorToUse = colors.green;
    } else if (colorLower.includes('yellow') || colorLower.includes('orange')) {
      colorToUse = colors.yellow;
    } else {
      // Default to red for any other color that includes 'red'
      colorToUse = colors.red;
    }
  }

  // Apply color if available
  const displayValue = colorToUse
    ? `<span style="color: ${colorToUse}">${value}</span>`
    : value;

  // Create tags array for comparisons
  const tags = [];

  // Add vsCAR tag if present
  if (vsCAR) {
    const absValue = Math.abs(parseFloat(vsCAR.value));
    // Use the same formatter as the main value for CAR comparisons for dollar values,
    // or the comparison formatter for percentage values
    const formattedValue =
      formatValue === formatDollarValue
        ? formatDollarValue(absValue)
        : formatComparisonValue(absValue);

    tags.push({
      isPositiveValue: parseFloat(vsCAR.value) >= 0,
      text: formattedValue,
      sideLabel: 'vs CAR',
      showArrow: parseFloat(vsCAR.value) !== 0,
    });
  }

  // Add period comparison tag if present
  if (periodComparison) {
    const absValue = Math.abs(parseFloat(periodComparison.value));
    tags.push({
      isPositiveValue: parseFloat(periodComparison.value) >= 0,
      text: formatComparisonValue(absValue),
      sideLabel: ytdCheckbox ? 'vs Last Year' : 'vs Last Month',
      showArrow: parseFloat(periodComparison.value) !== 0,
    });
  }

  // Format tag based on useTagsArray option
  let finalTag = null;
  if (tags.length > 0) {
    finalTag = useTagsArray ? tags : tags[0];
  }

  return createKpiTileItem(displayName, displayValue, finalTag);
};

export const getSectorLabel = (productId) => {
  if (productId === constants?.products?.ProfitToServe) {
    return constants.filterBarPlaceHolder.region;
  }
  return constants.filterBarPlaceHolder.sector;
};
